import { useContext } from 'react';
import { Context } from '../Context';
import type { ArticleIntro } from '../types';

export interface Tracking {
  sendReadyEvent(): void;
  registerImpressionTracking(): void;
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  sendClickEvent(this: void, articleIntro: ArticleIntro): void;
}

export function useTracking(
  ref: React.MutableRefObject<null>,
  headline: string,
  relatedElements: { elementName: string; elementValue: string }[],
  attributes?: {
    version?: string;
    implementer?: number;
    [key: string]: unknown;
  },
): Tracking {
  const { trackingService } = useContext(Context);
  const featureAppName = (typeof __FEATURE_APP_NAME__ === 'string' && __FEATURE_APP_NAME__) || '';
  const componentNameWithDashes = featureAppName.replace('fa-', '');
  const componentNameWithSpaces = componentNameWithDashes.replace(/-/g, ' ');
  const defaultComponentAdditions = {
    componentInfo: {
      // not accepted by tracking lib
      // componentID: 'bla',
    },
    attributes: {
      version: __FEATURE_APP_VERSION__,
      ...attributes,
    },
  };

  return {
    sendReadyEvent: () => {
      if (trackingService) {
        const readyEvent = {
          event: {
            attributes: {
              componentName: componentNameWithDashes,
              elementName: '',
              label: '',
              clickID: '',
              pos: '',
              targetURL: '',
              value: '',
            },
            eventInfo: {
              eventAction: 'feature_app_ready',
              eventName: `${componentNameWithSpaces} - feature app ready`,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        trackingService.track(readyEvent);
      }
    },
    registerImpressionTracking: () => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (trackingService && ref.current) {
        trackingService.registerImpressionTracking(ref.current, () => {
          return {
            event: {
              attributes: {
                componentName: componentNameWithDashes,
                elementName: '',
                label: '',
                clickID: '',
                pos: '',
                targetURL: '',
                value: headline,
                relatedElements,
              },
              eventInfo: {
                eventAction: 'impression',
                eventName: `${componentNameWithSpaces} - impression`,
              },
            },
            componentUpdate: defaultComponentAdditions,
          };
        });
      }
    },
    sendClickEvent: (articleIntro: ArticleIntro) => {
      if (trackingService) {
        const clickEvent = {
          event: {
            attributes: {
              componentName: componentNameWithDashes,
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              elementName: relatedElements && relatedElements.length === 1 ? 'button' : 'other',
              label: articleIntro.headline,
              clickID: '',
              pos: '',
              targetURL: articleIntro.teaserLink || '',
              value: headline,
              relatedElements: [
                {
                  elementName: 'article',
                  elementValue: `${articleIntro.index + 1}-${articleIntro.headline}`,
                },
              ],
            },
            eventInfo: {
              eventAction: 'internal_link',
              eventName: `${componentNameWithSpaces} - go to article`,
            },
          },
          componentUpdate: defaultComponentAdditions,
        };
        trackingService.track(clickEvent);
      }
    },
  };
}
